import React, { useState } from "react";
import { Repeat, LogIn, Trash2, Sparkles, Clock, RefreshCw } from "lucide-react";
import { Button } from "../shared/Button";
import { recurrentes as api } from "../../api/modulos.service";
import { coincide, plural } from "../../utils/texto";
import { estilos as est, Cabecera, Buscador, Vacio, Aviso, Ventana, Entrada, Seleccion, Area, useCarga } from "./base";
import { etiquetaTipoVisitante } from "../../utils/estados";

/**
 * VISITANTES RECURRENTES — la empleada, el jardinero, la niñera.
 *
 * Dos usos en una pantalla, según quién mire:
 *
 *  · El RESIDENTE los da de alta y los revoca.
 *  · El GUARDIA los usa: busca, ve si hoy le toca y registra la entrada de un
 *    toque. Por eso el botón grande es «Registrar entrada» y no «Editar».
 *
 * Cuando no le toca, **no se bloquea**: se enseña el motivo y el guardia
 * decide. El sistema avisa; la puerta la abre una persona.
 */

const DIAS = [
  { valor: 0, corto: "D" }, { valor: 1, corto: "L" }, { valor: 2, corto: "M" },
  { valor: 3, corto: "M" }, { valor: 4, corto: "J" }, { valor: 5, corto: "V" },
  { valor: 6, corto: "S" },
];

const FORM_VACIO = {
  name: "", phone: "", documentId: "", visitorType: "empleado_domestico",
  days: [1, 2, 3, 4, 5], startTime: "07:00", endTime: "17:00",
  validUntil: "", arrivalMode: "foot", plate: "", instructions: "",
  houseNumber: "", polygon: "",
};

export default function Recurrentes({ rol, catalogos, alRegistrarEntrada }) {
  const [busqueda, setBusqueda] = useState("");
  const [abierta, setAbierta] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [sugerencias, setSugerencias] = useState([]);

  const { datos, cargando, error: errorCarga, recargar } = useCarga(() => api.listar({}), []);
  const lista = datos?.recurringVisitors || [];

  const esGuardia = rol === "guard";
  const puedeAdministrar = rol === "resident" || rol === "admin";

  React.useEffect(() => {
    if (!puedeAdministrar) return;
    api.frecuentes().then((r) => setSugerencias(r.sugerencias || [])).catch(() => setSugerencias([]));
  }, [puedeAdministrar]);

  const filtrados = busqueda
    ? lista.filter((r) => coincide(r.name, busqueda))
    : lista;

  const cambiar = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });

  const alternarDia = (d) =>
    setForm((f) => ({
      ...f,
      days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d].sort(),
    }));

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    try {
      await api.crear({ ...form, validUntil: form.validUntil || null });
      setAbierta(false);
      setForm(FORM_VACIO);
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  };

  const registrarEntrada = async (r, forzar = false) => {
    try {
      const resultado = await api.registrarEntrada(r._id, { forzar });
      if (alRegistrarEntrada) alRegistrarEntrada(resultado.visit);
      recargar();
    } catch (err) {
      const datosErr = err?.response?.data;
      if (datosErr?.requiereConfirmacion) {
        // No se bloquea: se le explica al guardia y él decide.
        if (window.confirm(`${datosErr.message}. ¿Autoriza la entrada de todas formas?`)) {
          registrarEntrada(r, true);
        }
        return;
      }
      setError(datosErr?.message || "No se pudo registrar la entrada");
    }
  };

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Visitantes recurrentes"
        subtitulo={
          esGuardia
            ? "Busque a la persona y registre su entrada de un toque."
            : "La empleada, el jardinero, la niñera. Se autorizan una vez."
        }
        accion={puedeAdministrar ? "Autorizar a alguien" : null}
        alAccionar={() => setAbierta(true)}
        icono={Repeat}
      />

      <Buscador valor={busqueda} alCambiar={setBusqueda} marcador="Buscar por nombre…" />

      {error && <Aviso>{error}</Aviso>}

      {sugerencias.length > 0 && (
        <div style={{ ...est.tarjeta, borderColor: "var(--gold)" }}>
          <div style={est.tarjetaDato}>
            <Sparkles size={16} style={{ color: "var(--gold)" }} />
            <strong style={{ color: "var(--text)" }}>Vienen seguido y no están autorizados</strong>
          </div>
          {sugerencias.slice(0, 5).map((s) => (
            <div key={`${s.name}-${s.houseNumber}`} style={est.tarjetaDato}>
              <span>
                <strong style={{ color: "var(--text)" }}>{s.name}</strong> — {s.visitas} {plural(s.visitas, "visita")} en 30 días
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setForm({
                    ...FORM_VACIO,
                    name: s.name,
                    visitorType: s.visitorType || "empleado_domestico",
                    plate: s.plate || "",
                    arrivalMode: s.plate ? "vehicle" : "foot",
                  });
                  setAbierta(true);
                }}
              >
                Autorizar
              </Button>
            </div>
          ))}
        </div>
      )}

      {errorCarga ? (
        <Aviso>
          {errorCarga}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      ) : cargando ? (
        <Vacio>Cargando…</Vacio>
      ) : filtrados.length === 0 ? (
        <Vacio>
          {busqueda ? "Nadie coincide con esa búsqueda." : "Todavía no hay accesos recurrentes."}
        </Vacio>
      ) : (
        <div style={est.rejilla}>
          {filtrados.map((r) => (
            <div key={r._id} style={est.tarjeta}>
              <h3 style={est.tarjetaTitulo}>{r.name}</h3>
              <div style={est.tarjetaDato}>
                {catalogos?.tiposVisitante?.find((t) => t.valor === r.visitorType)?.etiqueta ||
                  /* Si el catálogo del servidor no cargó, el respaldo era el
                   * identificador crudo: «empleado_domestico» en pantalla. */
                  etiquetaTipoVisitante(r.visitorType)}
                {" · "}Casa {r.polygon}-{r.houseNumber}
              </div>
              <div style={est.tarjetaDato}>
                <Clock size={14} />
                {r.startTime}–{r.endTime}
                <span style={{ display: "flex", gap: "2px", marginLeft: "var(--space-2)" }}>
                  {DIAS.map((d) => (
                    <span
                      key={d.valor}
                      style={{
                        width: "18px",
                        height: "18px",
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "4px",
                        fontSize: "10px",
                        fontWeight: 700,
                        backgroundColor: r.days?.includes(d.valor) ? "var(--gold)" : "var(--bg-tertiary)",
                        color: r.days?.includes(d.valor) ? "#15171b" : "var(--text-tertiary)",
                      }}
                    >
                      {d.corto}
                    </span>
                  ))}
                </span>
              </div>
              {r.plate && <div style={est.tarjetaDato}>Placa {r.plate}</div>}

              <div
                style={{
                  ...est.tarjetaDato,
                  color: r.vigencia?.vigente ? "var(--success)" : "var(--warning)",
                  fontWeight: 600,
                }}
              >
                {r.vigencia?.vigente ? "Hoy le toca" : r.vigencia?.motivo}
              </div>

              <div style={est.tarjetaAcciones}>
                {esGuardia && (
                  <Button size="sm" variant="primary" leftIcon={LogIn} onClick={() => registrarEntrada(r)}>
                    Registrar entrada
                  </Button>
                )}
                {puedeAdministrar && (
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={Trash2}
                    onClick={() => {
                      if (window.confirm(`¿Revocar el acceso de ${r.name}?`)) {
                        api.revocar(r._id).then(recargar);
                      }
                    }}
                  >
                    Revocar
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Ventana
        titulo="Autorizar un visitante recurrente"
        abierta={abierta}
        alCerrar={() => setAbierta(false)}
        pie={
          <>
            <Button variant="ghost" onClick={() => setAbierta(false)}>Cancelar</Button>
            <Button variant="primary" onClick={guardar} loading={guardando}>Autorizar</Button>
          </>
        }
      >
        {error && <Aviso>{error}</Aviso>}

        <Entrada etiqueta="Nombre" value={form.name} onChange={cambiar("name")} required />

        <div style={est.fila}>
          <Seleccion
            etiqueta="Qué hace"
            value={form.visitorType}
            onChange={cambiar("visitorType")}
            opciones={catalogos?.tiposVisitante || []}
          />
          <Entrada etiqueta="Teléfono" value={form.phone} onChange={cambiar("phone")} placeholder="7000-0000" />
        </div>

        {rol === "admin" && (
          <div style={est.fila}>
            <Entrada etiqueta="Polígono" value={form.polygon} onChange={cambiar("polygon")} required />
            <Entrada etiqueta="Casa" value={form.houseNumber} onChange={cambiar("houseNumber")} required />
          </div>
        )}

        <div>
          <span style={est.etiqueta}>Días autorizados</span>
          <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
            {DIAS.map((d) => (
              <button
                key={d.valor}
                type="button"
                onClick={() => alternarDia(d.valor)}
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "var(--radius-md)",
                  border: "2px solid var(--border)",
                  backgroundColor: form.days.includes(d.valor) ? "var(--gold)" : "var(--bg-tertiary)",
                  color: form.days.includes(d.valor) ? "#15171b" : "var(--text-secondary)",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {d.corto}
              </button>
            ))}
          </div>
        </div>

        <div style={est.fila}>
          <Entrada etiqueta="Desde las" type="time" value={form.startTime} onChange={cambiar("startTime")} />
          <Entrada etiqueta="Hasta las" type="time" value={form.endTime} onChange={cambiar("endTime")} />
        </div>

        <div style={est.fila}>
          <Entrada
            etiqueta="Vence el (opcional)"
            type="date"
            value={form.validUntil}
            onChange={cambiar("validUntil")}
          />
          <Seleccion
            etiqueta="Cómo llega"
            value={form.arrivalMode}
            onChange={cambiar("arrivalMode")}
            opciones={[
              { valor: "foot", etiqueta: "A pie" },
              { valor: "vehicle", etiqueta: "En vehículo" },
              { valor: "motorcycle", etiqueta: "En moto" },
            ]}
          />
        </div>

        {form.arrivalMode !== "foot" && (
          <Entrada
            etiqueta="Placa"
            value={form.plate}
            onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })}
            placeholder="P123-456"
            required
          />
        )}

        <Area
          etiqueta="Instrucciones para la caseta"
          value={form.instructions}
          onChange={cambiar("instructions")}
          placeholder="Por ejemplo: que se estacione frente a la casa"
        />
      </Ventana>
    </div>
  );
}
